/**
 * A player's name, floating over their head.
 *
 * Howard: "there needs to display a username above players head." In a shared room
 * everyone is an animal, and two wolves are indistinguishable — without a name you
 * cannot tell who you are looking at, or find the person you came to play with.
 *
 * Drawn as a sprite so it always faces the camera, at a fixed world size so it shrinks
 * honestly with distance rather than following you around the screen.
 */
import * as THREE from 'three'

/** World height of the label. Tuned so it is readable across a clearing. */
const TAG_HEIGHT = 0.34
const PADDING = 18
const FONT = 64
/** Widest label the canvas holds; names are clipped to fit. */
const MAX_WIDTH = 1024
/** Beyond this distance the label stops shrinking. */
const HOLD_FROM = 11

export interface NameTag {
  sprite: THREE.Sprite
  /**
   * Hold the label at a constant on-screen size past HOLD_FROM metres. A sprite that
   * shrinks honestly with distance is unreadable across a clearing, which is exactly
   * when you need to know who that is.
   */
  faceCamera(distance: number): void
  /** Redraw for a new name or colour. Cheap enough to call only on change. */
  set(name: string, color: string): void
  dispose(): void
}

export function createNameTag(name: string, color: string): NameTag {
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')!
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  const material = new THREE.SpriteMaterial({ map: texture, transparent: true, depthWrite: false })
  const sprite = new THREE.Sprite(material)
  let aspect = 1

  // Fixed canvas, sized once. Resizing a canvas that already backs a GPU texture left
  // the previous name visible under the new one — a rename read as "Kater 2" rather
  // than "Kate". The label is fitted by scaling the sprite instead.
  canvas.width = MAX_WIDTH
  canvas.height = FONT + PADDING * 2

  const draw = (text: string, tint: string): void => {
    const label = text.trim().slice(0, 18) || 'Player'
    const height = canvas.height
    context.setTransform(1, 0, 0, 1, 0, 0)
    context.clearRect(0, 0, canvas.width, canvas.height)
    context.font = `700 ${FONT}px system-ui, sans-serif`
    context.textAlign = 'center'
    context.textBaseline = 'middle'
    const width = Math.min(MAX_WIDTH, Math.ceil(context.measureText(label).width) + PADDING * 2)
    // Draw centred in the fixed canvas, then crop to the used width with the sprite's
    // texture offset so the plate hugs the text.
    const left = (canvas.width - width) / 2

    // A dark plate behind the text, so a pale name stays readable against the sky and
    // a dark one stays readable against the ground.
    context.fillStyle = 'rgba(12, 16, 14, 0.62)'
    roundRect(context, left, 0, width, height, height * 0.34)
    context.fill()

    context.lineWidth = 8
    context.strokeStyle = 'rgba(0, 0, 0, 0.75)'
    context.strokeText(label, canvas.width / 2, height / 2 + 2)
    context.fillStyle = tint
    context.fillText(label, canvas.width / 2, height / 2 + 2)

    // Show only the part of the canvas the plate occupies.
    texture.repeat.set(width / canvas.width, 1)
    texture.offset.set(left / canvas.width, 0)
    texture.needsUpdate = true
    aspect = width / height
    sprite.scale.set(TAG_HEIGHT * aspect, TAG_HEIGHT, 1)
  }

  draw(name, color)

  return {
    sprite,
    set: draw,
    faceCamera(distance: number): void {
      const grow = Math.max(1, distance / HOLD_FROM)
      sprite.scale.set(TAG_HEIGHT * aspect * grow, TAG_HEIGHT * grow, 1)
    },
    dispose(): void {
      texture.dispose()
      material.dispose()
    },
  }
}

function roundRect(c: CanvasRenderingContext2D, x: number, y: number,
                   w: number, h: number, r: number): void {
  c.beginPath()
  c.moveTo(x + r, y)
  c.arcTo(x + w, y, x + w, y + h, r)
  c.arcTo(x + w, y + h, x, y + h, r)
  c.arcTo(x, y + h, x, y, r)
  c.arcTo(x, y, x + w, y, r)
  c.closePath()
}
